import { setCookie } from '@cian/utils/lib/browser/cookie';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { CONSULTING_FORM_SUBMITTED_COOKIE_NAME } from '../../constants/requestForm';
import { prepareNewbuildingBroker } from '../../containers/RequestFormModalWindow/utils/newbuildingBroker';
import { prepareTrackingParams } from '../../containers/RequestFormModalWindow/utils/prepareTrackingParams';
import {
  selectFormAdditionalServices,
  selectFormPhone,
  selectFormPhoneVerificationCode,
  selectFormPriceRange,
  selectFormRegion,
  selectFormRequirements,
  selectFormRoomCount,
  selectFormStep,
} from '../../selectors/requestFormV2';
import { selectUserPhoneConfirmed } from '../../selectors/user';
import { selectUtm } from '../../selectors/utm';
import { createConsultingRequest } from '../../services/consultingRequest';
import { setErrors, setFormStep, setHhConversionId } from '../../slices';
import { IThunkApi } from '../../types/redux';
import { EFormStep } from '../../types/requestForm/requestFormV2';
import { clearPhone } from '../../utils/phone';
import { trackCallbackClickAction } from '../analytics/trackCallbackClickAction';
import { requestPhoneVerificationCode } from '../phoneVerification/requestPhoneVerificationCode';
interface IThunkApiExtended extends IThunkApi {
  rejectValue: void;
}

export const createConsultingRequestWithVerification = createAsyncThunk<void, void, IThunkApiExtended>(
  'consultingRequest/createConsultingRequest',
  async (parameters, { extra: context, dispatch, getState }) => {
    const { httpApi } = context;
    const state = getState();
    const phone = selectFormPhone(state);
    const region = selectFormRegion(state);
    const requirements = selectFormRequirements(state);
    const roomCount = selectFormRoomCount(state);
    const priceRange = selectFormPriceRange(state);
    const additionalServices = selectFormAdditionalServices(state);
    const step = selectFormStep(state);
    const userPhoneConfirmed = selectUserPhoneConfirmed(state);
    const validationCode = selectFormPhoneVerificationCode(state);

    try {
      if (step === EFormStep.BookingExists) {
        return;
      }

      const preparedPhone = phone ? `+${clearPhone(phone)}` : '';
      const newbuildingBroker = prepareNewbuildingBroker(context.config);
      const utm = selectUtm(state) ?? undefined;

      if (!userPhoneConfirmed && !validationCode) {
        dispatch(requestPhoneVerificationCode());

        return;
      }

      const response = await createConsultingRequest({
        httpApi,
        parameters: {
          source: 'landingKpn',
          phone: preparedPhone,
          regionId: region?.id,
          requirements,
          roomCount,
          priceRange,
          additionalServices,
          newbuildingBroker,
          utm,
          validationCode,
        },
      });

      // Устанавливаем куку на сутки о том, что форма отправлена
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      setCookie(CONSULTING_FORM_SUBMITTED_COOKIE_NAME, '1', { expires: tomorrow });

      dispatch(setHhConversionId({ hhConversionId: response.hhConversionId }));
      dispatch(
        trackCallbackClickAction(
          prepareTrackingParams({ phone: phone ?? '', region, requirements: requirements ?? '' }),
        ),
      );
      dispatch(setFormStep(EFormStep.Success));
    } catch (error) {
      const { customerPhone, missingValidationCode, invalidCustomerPhone, invalidValidationCode } = error.details;
      if (missingValidationCode) {
        dispatch(requestPhoneVerificationCode());
        dispatch(setFormStep(EFormStep.PhoneVerification));

        return;
      }

      if (!customerPhone && !invalidCustomerPhone && !invalidValidationCode && !missingValidationCode) {
        dispatch(setFormStep(EFormStep.Error));
      }

      if (invalidValidationCode) {
        dispatch(setFormStep(EFormStep.PhoneVerification));
      }

      if (invalidCustomerPhone || customerPhone) {
        dispatch(setFormStep(EFormStep.Contacts));
      }

      dispatch(setErrors({ phone: customerPhone || invalidCustomerPhone, phoneVerification: invalidValidationCode }));
    }
  },
);
